import React from 'react';
import { Building2, Award, TrendingUp } from 'lucide-react';

const TrustSection = () => {
  const experiences = [
    {
      icon: Award,
      stat: 'Qualität',
      label: 'Unser Versprechen',
      description: 'Professionelle Standards, geschulte Teams und hochwertige Reinigungsmittel – von Anfang an setzen wir auf Qualität statt Kompromisse.'
    },
    {
      icon: Building2,
      stat: 'Vielfältig',
      label: 'Unsere Expertise',
      description: 'Vom Büro über Restaurants bis zur Wohnimmobilie – wir verstehen die individuellen Anforderungen verschiedener Branchen und Objekttypen.'
    },
    {
      icon: TrendingUp,
      stat: 'Engagement',
      label: 'Unsere Arbeitsweise',
      description: 'Feste Ansprechpartner, transparente Prozesse und schnelle Reaktionszeiten – wir arbeiten mit Leidenschaft für Ihr Objekt.'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-[#F9FAFB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 fade-in">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A2846] mb-6">
            Professionalität, die überzeugt
          </h2>
          <div 
            className="w-32 h-1.5 mx-auto mb-8 rounded-full"
            style={{ 
              background: 'linear-gradient(90deg, #D4AF37 0%, #FFD700 50%, #F4D03F 100%)',
              boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)'
            }} 
          />
          <p className="text-xl text-[#1F2937] max-w-3xl mx-auto leading-relaxed">
            Mit geschultem Personal, modernen Methoden und einem klaren Fokus auf Qualität bieten wir Ihnen professionelle Gebäudereinigung auf höchstem Niveau
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {experiences.map((exp, index) => {
            const Icon = exp.icon;
            return (
              <div
                key={index}
                className="text-center p-8 bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 border-2 border-gray-100"
              >
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{
                    background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                    boxShadow: '0 8px 25px rgba(212, 175, 55, 0.4)'
                  }}
                >
                  <Icon className="w-10 h-10 text-[#0A2846]" />
                </div>
                <div 
                  className="text-4xl font-bold mb-2"
                  style={{
                    background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  {exp.stat}
                </div>
                <div className="text-xl font-semibold text-[#0A2846] mb-3">{exp.label}</div>
                <p className="text-[#1F2937]/70 leading-relaxed">{exp.description}</p>
              </div>
            );
          })}
        </div>

        {/* Expertise Section */}
        <div>
          <h3 className="text-3xl font-bold text-[#0A2846] text-center mb-8">
            Breite Expertise für verschiedene Bereiche
          </h3>
          <p className="text-lg text-[#1F2937]/80 text-center mb-12 max-w-3xl mx-auto leading-relaxed">
            Wir arbeiten erfolgreich mit Hausverwaltungen, Gewerbetreibenden und privaten Auftraggebern zusammen und verstehen die spezifischen Anforderungen jeder Branche.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4" style={{ borderColor: '#D4AF37' }}>
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                  boxShadow: '0 8px 20px rgba(212, 175, 55, 0.4)'
                }}
              >
                <span className="text-2xl font-bold text-[#0A2846]">1</span>
              </div>
              <h4 className="text-xl font-semibold text-[#0A2846] mb-3 text-center">
                Branchenvielfalt
              </h4>
              <p className="text-[#1F2937]/80 leading-relaxed text-center">
                Wir kennen die unterschiedlichen Anforderungen: Von Hausverwaltungen über Gastronomie bis zu Gewerbeimmobilien – jede Branche erhält maßgeschneiderte Lösungen.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4" style={{ borderColor: '#D4AF37' }}>
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                  boxShadow: '0 8px 20px rgba(212, 175, 55, 0.4)'
                }}
              >
                <span className="text-2xl font-bold text-[#0A2846]">2</span>
              </div>
              <h4 className="text-xl font-semibold text-[#0A2846] mb-3 text-center">
                Dokumentierte Prozesse
              </h4>
              <p className="text-[#1F2937]/80 leading-relaxed text-center">
                Lückenlose Dokumentation, transparente Leistungsnachweise und feste Ansprechpartner – wichtig für professionelle Kunden und langfristige Zusammenarbeit.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4" style={{ borderColor: '#D4AF37' }}>
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                  boxShadow: '0 8px 20px rgba(212, 175, 55, 0.4)'
                }}
              >
                <span className="text-2xl font-bold text-[#0A2846]">3</span>
              </div>
              <h4 className="text-xl font-semibold text-[#0A2846] mb-3 text-center">
                Verlässliche Partnerschaften
              </h4>
              <p className="text-[#1F2937]/80 leading-relaxed text-center">
                Wir bauen auf langfristige Zusammenarbeit mit unseren Kunden – Vertrauen entsteht durch konstante Qualität und Zuverlässigkeit.
              </p>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-20 text-center">
          <div 
            className="p-12 rounded-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%)',
              border: '2px solid rgba(212, 175, 55, 0.2)'
            }}
          >
            <h3 className="text-3xl font-bold text-[#0A2846] mb-6">
              Starten Sie mit einem verlässlichen Partner
            </h3>
            <p className="text-lg text-[#1F2937] mb-8 max-w-2xl mx-auto leading-relaxed">
              Erzählen Sie uns von Ihrem Objekt. Wir erstellen Ihnen ein transparentes Angebot und beraten Sie persönlich – unverbindlich und kostenfrei.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;